import TeamMemberCard from "./TeamMemberCard";
import toast from 'react-hot-toast';
import { Users } from "lucide-react";
import Pagination from "../../../components/dashboard/dashboard/Pagination";
import Modal from "./Modal";
import React, { useState } from "react";

const MEMBERS_PER_PAGE = 9;

const TeamMembersGrid = ({
    teamMembers,
    teamsLoading,
    isLoadingMembers,
    onRemove,
    currentUserId,
    onMakeModerator = () => {},
    onAccept = () => {},
    onDecline = () => {},
    currentUserRole,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingRemoveId, setPendingRemoveId] = useState(null);
    const [search, setSearch] = useState("");
    const [sortField, setSortField] = useState('created_at'); // 'created_at', 'name', 'email'
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
    const [actionRequiredFirst, setActionRequiredFirst] = useState(false);

    // Filter and sort members
    let filteredMembers = teamMembers.filter(m => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
            (m.name && m.name.toLowerCase().includes(q)) ||
            (m.email && m.email.toLowerCase().includes(q))
        );
    });
    // Action required filter
    if (actionRequiredFirst) {
        filteredMembers = filteredMembers.filter(m => m.pivot && !m.pivot.accepted);
    }
    let sortedMembers = filteredMembers.slice();
    // Sort by selected field and direction
    sortedMembers.sort((a, b) => {
        let aValue, bValue;
        if (sortField === 'created_at') {
            aValue = new Date(a.created_at);
            bValue = new Date(b.created_at);
        } else if (sortField === 'name') {
            aValue = a.name || '';
            bValue = b.name || '';
        } else {
            aValue = a[sortField] || '';
            bValue = b[sortField] || '';
        }
        if (sortField === 'created_at') {
            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        return sortDirection === 'asc' 
            ? String(aValue).localeCompare(String(bValue)) 
            : String(bValue).localeCompare(String(aValue));
    });
    const totalPages = Math.max(1, Math.ceil(sortedMembers.length / MEMBERS_PER_PAGE));
    const pagedMembers = sortedMembers.slice(
        (currentPage - 1) * MEMBERS_PER_PAGE,
        currentPage * MEMBERS_PER_PAGE
    );
    // Reset to page 1 if teamMembers or filter changes and currentPage is now out of range
    React.useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(1);
    }, [teamMembers, search, totalPages]);

    // Handler to trigger confirmation
    const handleRequestRemove = (memberId) => {
        setPendingRemoveId(memberId);
        setShowConfirm(true);
    };
    // Handler to confirm deletion
    const handleConfirmRemove = async () => {
        if (pendingRemoveId !== null) {
            try {
                await onRemove(pendingRemoveId);
            } catch (error) {
                if (error?.response?.status === 403 && error?.response?.data?.message?.includes('Owners cannot be removed')) {
                    toast.error('You cannot remove an owner from the team. Please delete the team instead.');
                } else {
                    toast.error('Failed to remove member.');
                }
            }
        }
        setShowConfirm(false);
        setPendingRemoveId(null);
    };
    // Handler to cancel
    const handleCancelRemove = () => {
        setShowConfirm(false);
        setPendingRemoveId(null);
    };
    // Handler to toggle moderator status
    const handleToggleModerator = (memberId) => {
        const member = teamMembers.find(m => m.id === memberId);
        if (member && member.pivot) {
            const isCurrentlyModerator = member.pivot.role === 'moderator';
            onMakeModerator(memberId, !isCurrentlyModerator);
        }
    };
    // Accept member request (remove 'requested' field)
    const handleAccept = (memberId) => {
        if (typeof onAccept === 'function') onAccept(memberId);
    };
    // Decline member request
    const handleDecline = (memberId) => {
        if (typeof onDecline === 'function') onDecline(memberId);
    };

    if (isLoadingMembers || teamsLoading) {
        return (
            <>
                {/* Title Skeleton */}
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex flex-col sm:flex-row gap-2 w-full">
                        <div className="flex-1 min-w-0">
                            <div className="h-10 w-full bg-gray-200 dark:bg-gray-800 rounded-xl mb-2 animate-pulse"></div>
                        </div>
                        <div className="h-10 w-32 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
                    </div>
                </div>
                {/* Search bar skeleton */}
                <div className="mb-4">
                    <div className="h-10 w-full bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
                </div>
                {/* Grid of loading cards skeletons */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[...Array(3)].map((_, index) => (
                        <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm animate-pulse">
                            <div className="rounded-t-lg px-3 py-2.5 mb-3 bg-gray-100 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
                                <div className="flex items-center gap-2">
                                    <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                </div>
                            </div>
                            <div className="p-3">
                                <div className="flex items-center justify-between gap-2 mb-4">
                                    <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                </div>
                                <div className="flex items-center justify-between gap-2 mb-3">
                                    <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                    <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                </div>
                            </div>
                            <div className="px-3 py-2 bg-gray-100 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 rounded-b-lg">
                                <div className="flex justify-end">
                                    <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    }
    // Show search bar and handle empty filtered results
    return (
        <>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex flex-col sm:flex-row gap-2 w-full">
                    {/* Search input */}
                    <div className="relative flex-1 min-w-0">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            {/* Search icon */}
                            <svg className="w-5 h-5 text-sky-400 dark:text-sky-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                <circle cx="11" cy="11" r="7" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        </span>
                        <input
                            type="text"
                            className="w-full pl-10 pr-10 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 shadow focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 transition-all duration-200 focus:scale-[1.03]"
                            placeholder="Search members by name"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            aria-label="Search team members"
                        />
                        {search && (
                            <button
                                type="button"
                                tabIndex={0}
                                aria-label="Clear search"
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-sky-500 dark:hover:text-sky-300 focus:outline-none"
                                onClick={() => setSearch("")}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </div>
                    {/* Segmented sort controls */}
                    <div className="flex flex-wrap gap-2 items-center">
                        {[
                            { id: 'created_at', label: 'Joined', icon: <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"/></svg> },
                            { id: 'name', label: 'Name', icon: <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z"/><path d="M12 14a7 7 0 0 0-7 7"/><path d="M12 14a7 7 0 0 1 7 7"/></svg> },
                        ].map(option => (
                            <button
                                key={option.id}
                                onClick={() => {
                                    if (sortField === option.id) {
                                        setSortDirection(current => current === 'asc' ? 'desc' : 'asc');
                                    } else {
                                        setSortField(option.id);
                                        setSortDirection('asc');
                                    }
                                }}
                                className={`px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1
                                    ${sortField === option.id
                                        ? 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300 shadow-sm'
                                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}
                                `}
                                aria-pressed={sortField === option.id}
                            >
                                {option.icon} {option.label}
                                {sortField === option.id && (
                                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                )}
                            </button>
                        ))}
                    </div>
                    {/* Action required pill toggle - only for owner or moderator */}
                    {(currentUserRole === 'owner' || currentUserRole === 'moderator') && (
                        <button
                            type="button"
                            aria-pressed={actionRequiredFirst}
                            onClick={() => setActionRequiredFirst(v => !v)}
                            className={`flex items-center px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 border focus:outline-none focus:ring-2 focus:ring-sky-400 shadow-sm ml-1
                                ${actionRequiredFirst
                                    ? 'bg-gradient-to-r from-sky-500 to-sky-600 text-white border-sky-500 dark:border-sky-700'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-sky-300 dark:hover:border-sky-400'}
                            `}
                            tabIndex={0}
                        >
                            <svg className={`w-4 h-4 mr-2 ${actionRequiredFirst ? 'text-white' : 'text-sky-500 dark:text-sky-300'}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M12 8v4m0 4h.01" strokeLinecap="round" strokeLinejoin="round" />
                                <circle cx="12" cy="12" r="10" />
                            </svg>
                            Action required
                        </button>
                    )}
                </div>
            </div>
            {filteredMembers.length === 0 ? (
                <div className="text-center py-8 rounded-lg">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">No members found matching your search.</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {pagedMembers.map(member => (
                            <TeamMemberCard 
                                key={member.id}
                                member={member}
                                currentUserRole={currentUserRole}
                                currentUserId={currentUserId}
                                onRemove={handleRequestRemove}
                                onMakeModerator={handleToggleModerator}
                                onAccept={handleAccept}
                                onDecline={handleDecline}
                            />
                        ))}
                    </div>
                    {filteredMembers.length > MEMBERS_PER_PAGE && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </>
            )}
            {/* Confirmation Modal */}
            <Modal isOpen={showConfirm} onClose={handleCancelRemove}>
                <h2 className="text-lg font-semibold mb-4 dark:text-white">Remove Team Member</h2>
                <p className="mb-2 text-gray-700 dark:text-gray-200">
                    Are you sure you want to remove
                    <span className="font-bold text-red-600 dark:text-red-400"> {teamMembers.find(m => m.id === pendingRemoveId)?.name}</span>
                    {" "}from the team? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-700"
                        onClick={handleCancelRemove}
                    >
                        Cancel
                    </button>
                    <button
                        className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                        onClick={handleConfirmRemove}
                    >
                        Remove
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default TeamMembersGrid;
